import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';

import { EmptyStateView, Label, Pagination } from '@atlas/ui';

import { crownIcon, leaderboardIcon, PLAYERS_PER_PAGE, starsIcon } from './shares';

const Leaderboard = ({ players = [], playersPerPage = PLAYERS_PER_PAGE }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedPlayers, setPaginatedPlayers] = useState([]);

  const myUserInPagination = paginatedPlayers.some(player => player.is_me);
  const myUserInListIndex = players.findIndex(player => player.is_me);
  const paginate = useCallback(
    pageNumber => {
      setCurrentPage(pageNumber);
      const indexOfFirstPlayer = (pageNumber - 1) * playersPerPage;
      const indexOfLastPlayer = indexOfFirstPlayer + playersPerPage;
      setPaginatedPlayers(players.slice(indexOfFirstPlayer, indexOfLastPlayer));
    },
    [players, playersPerPage],
  );
  const totalPages = Math.ceil(players.length / playersPerPage);

  const changePage = page => paginate(page + 1);

  useEffect(() => {
    paginate(currentPage);
  }, [players, currentPage, paginate]);

  const bodyCls = cx({
    'smartico-table-fixed-leaderboard-default': totalPages > 1 && myUserInListIndex < 0,
    'smartico-table-fixed-leaderboard-with-me': totalPages > 1 && myUserInListIndex > -1,
  });

  const userContentRow = !myUserInPagination && !!players[myUserInListIndex] && (
    <div className="smartico-table-row smartico-table-row-my smartico-table-row-highlited">
      <div className="smartico-table-text smartico-table-column-first">
        {players[myUserInListIndex].position}
      </div>
      <div className="smartico-table-score smartico-table-column">
        <div className="smartico-table-img smartico-table-img-leaderboard">
          <img
            alt={players[myUserInListIndex].public_username}
            src={players[myUserInListIndex].avatar_url}
          />
        </div>
        {!!players[myUserInListIndex].public_username && (
          <div className="smartico-table-text">{players[myUserInListIndex].public_username}</div>
        )}
      </div>
      <div className="smartico-leaderboard-score smartico-table-column-last">
        {players[myUserInListIndex].scores}
      </div>
    </div>
  );

  if (!players.length) {
    return (
      <EmptyStateView
        className="list-empty"
        description="smartico_tournaments_leaderboard_empty_description"
        title="smartico_tournaments_leaderboard_empty_title"
      />
    );
  }

  return (
    <div className="smartico-tournament-leaderboard-wrapper">
      <div className="smartico-leaderboard-top">
        <div className="smartico-leaderboard-place smartico-leaderboard-place-first">
          <div className="smartico-leaderboard-leader">
            <img
              alt="crown"
              className="smartico-leaderboard-crown"
              height={24}
              src={crownIcon}
              width={24}
            />
            <img
              alt="stars"
              className="smartico-leaderboard-stars"
              height={36}
              src={starsIcon}
              width={64}
            />
            <img
              alt={players[0].public_username}
              className="smartico-leaderboard-avatar"
              height={40}
              src={players[0].avatar_url}
              width={40}
            />
          </div>
          <div className="smartico-leaderboard-name">{players[0].public_username}</div>
          <div className="smartico-leaderboard-score">{players[0].scores}</div>
        </div>
        {!!players[1] && (
          <div className="smartico-leaderboard-place smartico-leaderboard-place-second">
            <img
              alt={players[1].public_username}
              className="smartico-leaderboard-avatar"
              height={40}
              src={players[1].avatar_url}
              width={40}
            />
            <div className="smartico-leaderboard-name">{players[1].public_username}</div>
            <div className="smartico-leaderboard-score">{players[1].scores}</div>
          </div>
        )}
        {!!players[2] && (
          <div className="smartico-leaderboard-place smartico-leaderboard-place-third">
            <img
              alt={players[2].public_username}
              className="smartico-leaderboard-avatar"
              height={40}
              src={players[2].avatar_url}
              width={40}
            />
            <div className="smartico-leaderboard-name">{players[2].public_username}</div>
            <div className="smartico-leaderboard-score">{players[2].scores}</div>
          </div>
        )}
        <img
          alt="LeaderBoard"
          className="smartico-leaderboard-board"
          height={117}
          src={leaderboardIcon}
          width={296}
        />
      </div>
      <div className="smartico-table">
        <div className="smartico-table-header">
          <Label
            className="smartico-table-header-title smartico-table-column-first"
            message="smartico_tournaments_leaderboard_rank"
          />
          <Label
            className="smartico-table-header-title smartico-table-column"
            message="smartico_tournaments_leaderboard_player"
          />
          <Label
            className="smartico-table-header-title smartico-table-column-last"
            message="smartico_tournaments_leaderboard_score"
          />
        </div>
        <div className={bodyCls}>
          {myUserInListIndex > -1 &&
            players[myUserInListIndex].position < paginatedPlayers[0]?.position &&
            userContentRow}
          {paginatedPlayers.map((player, index) => (
            <div
              className={`smartico-table-row ${player.is_me ? 'smartico-table-row-my' : ''}`}
              key={index * 2}
            >
              <div className="smartico-table-text smartico-table-column-first">
                {player.position}
              </div>
              <div className="smartico-table-score smartico-table-column">
                <div className="smartico-table-img smartico-table-img-leaderboard">
                  <img alt={player.public_username} src={player.avatar_url} />
                </div>
                {!!player.public_username && (
                  <div className="smartico-table-text">{player.public_username}</div>
                )}
              </div>
              <div
                className={`${player.is_me ? 'smartico-leaderboard-score smartico-leaderboard-score-main' : 'smartico-table-text'} smartico-table-column-last`}
              >
                {player.scores}
              </div>
            </div>
          ))}
          {myUserInListIndex > -1 &&
            players[myUserInListIndex].position >
              paginatedPlayers[paginatedPlayers.length - 1]?.position &&
            userContentRow}
        </div>
        <Pagination
          changeCurrentPage={changePage}
          currentPage={currentPage - 1}
          pages={totalPages}
        />
      </div>
    </div>
  );
};

export default Leaderboard;
